import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import axios from 'axios';
import { ArrowLeft, Pencil, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Editor } from '@/components/ui/editor';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SourceBreadcrumb } from '@/components/shared';
import { useSourceById, useSourceContent, useUpdateSource } from '@/hooks/use-sources';
import { usePermissions } from '@/hooks/use-permissions';
import { storageService } from '@/services';
import { SourceFileViewer, SourceGoogleViewer, SourceTypeIcon, getFileExtension } from '@/modules/sources';
import { SOURCE_TYPE_LABELS } from '@/modules/sources';
import { SourceKnowledgeSheet } from '@/modules/knowledge-promotion/source-knowledge-sheet';

const TEXT_FILE_EXTENSIONS = ['md', 'txt'];

export default function SourceDetailRoute() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { data: source, isLoading } = useSourceById(id ?? '');
	const updateSourceMutation = useUpdateSource();
	const { can } = usePermissions();
	const canEdit = can('edit', 'sources');
	// Its knowledge pipeline: `?promote=1` opens the drawer, as links from elsewhere do.
	const canPromote = can('view', 'promoted-blueprint');
	const [searchParams, setSearchParams] = useSearchParams();
	const promoteOpen = canPromote && searchParams.get('promote') === '1';
	const setPromoteOpen = (open: boolean) =>
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (open) next.set('promote', '1');
				else next.delete('promote');
				return next;
			},
			{ replace: true }
		);

	const isArticle = source?.type === 'ARTICLE';
	const isLink = source?.type === 'LINK';
	const fileExt = source ? getFileExtension(source) : '';
	const isTextFile = source?.type === 'FILE' && TEXT_FILE_EXTENSIONS.includes(fileExt);
	const isEditableText = isArticle || isTextFile;
	// PDF/DOCX/XLSX/Google Doc-Sheet previews render their own full page/sheet — give them the
	// entire content area instead of the reading-width padding used for editable text content.
	const isRendererView = !isArticle && !isLink && !isTextFile;

	const { data: contentData, isLoading: isContentLoading } = useSourceContent(id ?? '', !!isLink);

	const [isEditing, setIsEditing] = useState(false);
	const [name, setName] = useState('');
	const [content, setContent] = useState('');
	const [fileTextContent, setFileTextContent] = useState<string | null>(null);
	const [isFileContentLoading, setIsFileContentLoading] = useState(false);
	const [showDiscardDialog, setShowDiscardDialog] = useState(false);
	const [showSaveDialog, setShowSaveDialog] = useState(false);
	const loadedRef = useRef<string | undefined>(undefined);

	const originalContent = isArticle ? (source?.value ?? '') : (fileTextContent ?? '');
	const hasUnsavedChanges = isEditing && (content !== originalContent || name !== (source?.name ?? ''));

	useEffect(() => {
		if (!source || source.id === loadedRef.current) return;
		if (isArticle) setContent(source.value);
		setName(source.name);
		loadedRef.current = source.id;
	}, [source, isArticle]);

	useEffect(() => {
		if (!source || !isTextFile) return;
		let cancelled = false;
		setIsFileContentLoading(true);
		(async () => {
			try {
				const { url } = await storageService.generateDownloadUrl(source.value);
				const response = await axios.get<string>(url, { responseType: 'text' });
				if (cancelled) return;
				setFileTextContent(response.data);
				setContent(response.data);
			} finally {
				if (!cancelled) setIsFileContentLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [source?.id, isTextFile]);

	const handleEdit = () => {
		setContent(isArticle ? (source?.value ?? '') : (fileTextContent ?? ''));
		setName(source?.name ?? '');
		setIsEditing(true);
	};

	const discardEdits = () => {
		setIsEditing(false);
		setContent(isArticle ? (source?.value ?? '') : (fileTextContent ?? ''));
		setName(source?.name ?? '');
	};

	const handleCancel = () => {
		if (hasUnsavedChanges) {
			setShowDiscardDialog(true);
			return;
		}
		discardEdits();
	};

	const confirmDiscard = () => {
		setShowDiscardDialog(false);
		discardEdits();
	};

	const performSave = async () => {
		if (!source) return;
		const trimmedName = name.trim();
		try {
			if (isArticle) {
				await updateSourceMutation.mutateAsync({ id: source.id, data: { value: content, name: trimmedName } });
			} else if (isTextFile) {
				const fileName = (source.metadata?.['fileName'] as string | undefined) ?? source.name;
				const uploadUrlData = await storageService.generateUploadUrl(fileName, 'text/plain', { key: source.value });
				await storageService.uploadFile(uploadUrlData.url, new File([content], fileName, { type: 'text/plain' }));
				// value is unchanged, but re-sending it forces the backend to re-queue indexing
				await updateSourceMutation.mutateAsync({ id: source.id, data: { value: source.value, name: trimmedName } });
				setFileTextContent(content);
			}
			toast.success('Source updated successfully');
			setIsEditing(false);
			setShowSaveDialog(false);
		} catch {
			toast.error('Failed to update source');
		}
	};

	const handleSave = () => {
		setShowSaveDialog(true);
	};

	const isSaving = updateSourceMutation.isPending;

	if (isLoading || !source) {
		return (
			<div className='flex h-screen overflow-hidden'>
				<div className='flex-1 flex flex-col overflow-hidden'>
					<div className='flex items-center gap-4 p-4 border-b'>
						<Skeleton className='h-8 w-8 rounded' />
						<Skeleton className='h-5 w-48' />
						<Skeleton className='h-5 w-20 rounded-full' />
					</div>
					<div className='px-15 pt-8 space-y-4'>
						<Skeleton className='h-4 w-full' />
						<Skeleton className='h-4 w-full' />
						<Skeleton className='h-4 w-2/3' />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='flex h-screen overflow-hidden'>
			<div className='flex-1 flex flex-col overflow-hidden'>
				<SourceBreadcrumb label='Knowledge' />
				{/* Header */}
				<div className='flex items-center gap-4 p-4 border-b'>
					<div className='relative flex-1 flex items-center gap-3 h-8 min-w-0'>
						{/* navigate(-1) instead of a hardcoded '/sources?tab=knowledge' — a fixed URL would
						drop whatever category/subcategory/page filter was active on the list you came
						from, same issue the browser Back button had before it was fixed to preserve that
						state via the URL. */}
						<Button className='cursor-pointer border-none shadow-none shrink-0' variant='outline' size='icon' onClick={() => navigate(-1)}>
							<ArrowLeft className='size-5' />
						</Button>
						<SourceTypeIcon type={source.type} className='size-4 text-muted-foreground shrink-0' />
						{isEditableText && isEditing ? (
							<Input
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder='Untitled'
								className='h-8 text-base font-semibold'
							/>
						) : (
							<div className='p-2 rounded-md text-base font-semibold truncate' title={source.name}>
								{source.name}
							</div>
						)}
						<Badge variant='outline' className='shrink-0 text-xs font-medium px-3 py-0.5 rounded-full border-0 bg-gray-100 text-gray-700'>
							{SOURCE_TYPE_LABELS[source.type] ?? source.type}
						</Badge>
					</div>
					{canPromote && !isEditing && (
						<Button className='cursor-pointer text-xs' variant='outline' onClick={() => setPromoteOpen(true)}>
							<Sparkles className='h-3 w-3' /> Promote knowledge
						</Button>
					)}
					{isEditableText && (
						<div className='flex items-center gap-2 h-8'>
							{isEditing ? (
								<>
									<Button className='text-xs' variant='outline' onClick={handleCancel} disabled={isSaving}>
										Cancel
									</Button>
									<Button className='text-xs' onClick={handleSave} disabled={isSaving || !canEdit || !name.trim()}>
										Save
									</Button>
								</>
							) : (
								<Button className='cursor-pointer text-xs' variant='outline' onClick={handleEdit} disabled={!canEdit}>
									<Pencil className='h-3 w-3' /> Edit
								</Button>
							)}
						</div>
					)}
				</div>

				{/* Content */}
				<div className={`flex-1 overflow-y-auto ${isRendererView ? '' : 'pt-8 px-15 pb-8'}`}>
					{isArticle ? (
						<Editor
							placeholder='Start writing…'
							value={isEditing ? content : source.value}
							contentType='markdown'
							onChange={setContent}
							readOnly={!isEditing}
							showToolbar={isEditing}
							className='border-0! ring-0 rounded-none'
						/>
					) : isLink ? (
						isContentLoading ? (
							<Skeleton className='h-[60vh] w-full' />
						) : (
							<Editor
								placeholder='No extracted content yet — this source may still be indexing.'
								value={contentData?.content ?? ''}
								contentType='markdown'
								readOnly
								showToolbar={false}
								className='border-0! ring-0 rounded-none'
							/>
						)
					) : isTextFile ? (
						isFileContentLoading ? (
							<Skeleton className='h-[60vh] w-full' />
						) : (
							<Editor
								placeholder='Start writing…'
								value={isEditing ? content : (fileTextContent ?? '')}
								contentType='markdown'
								onChange={setContent}
								readOnly={!isEditing}
								showToolbar={isEditing}
								className='border-0! ring-0 rounded-none'
							/>
						)
					) : source.type === 'FILE' ? (
						<SourceFileViewer source={source} />
					) : (
						<SourceGoogleViewer source={source} />
					)}
				</div>
			</div>

			{canPromote && (
				<SourceKnowledgeSheet
					open={promoteOpen}
					onOpenChange={setPromoteOpen}
					sourceId={source.id}
					sourceName={source.name}
				/>
			)}

			<Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
				<DialogContent className='max-w-sm p-4'>
					<DialogHeader>
						<DialogTitle className='text-sm font-semibold'>Discard changes?</DialogTitle>
						<p className='text-xs text-muted-foreground'>
							You have unsaved changes. If you leave now, they&apos;ll be lost. Are you sure you want to cancel?
						</p>
					</DialogHeader>
					<div className='flex gap-2 justify-end'>
						<Button variant='outline' className='text-xs' onClick={confirmDiscard}>
							Discard
						</Button>
						<Button className='text-xs' onClick={() => setShowDiscardDialog(false)}>
							Keep Editing
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
				<DialogContent className='max-w-sm p-4'>
					<DialogHeader>
						<DialogTitle className='text-sm font-semibold'>Save changes?</DialogTitle>
						<p className='text-xs text-muted-foreground'>
							Are you sure you want to save these changes? This will update the content used by Kim AI.
						</p>
					</DialogHeader>
					<div className='flex gap-2 justify-end'>
						<Button variant='outline' className='text-xs' onClick={() => setShowSaveDialog(false)} disabled={isSaving}>
							Cancel
						</Button>
						<Button className='text-xs' onClick={performSave} disabled={isSaving}>
							{isSaving ? 'Saving…' : 'Save'}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
